<template>
  <b-card no-body>

    <!-- ── Toolbar ── -->
    <div class="m-2">
      <b-row>
        <b-col cols="12" md="9" class="d-flex align-items-center flex-wrap mb-1 mb-md-0" style="gap:0.5rem">

          <label class="mb-0">Entries</label>
          <v-select
            v-model="perPage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector"
          />

          <b-button
            :variant="sortFav ? 'warning' : 'outline-warning'"
            class="btn-icon"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            @click="ChageFav"
          >
            <feather-icon icon="StarIcon" />
          </b-button>

          <b-button
            :variant="sortPublic ? 'danger' : 'outline-danger'"
            v-ripple.400="'rgba(234, 84, 85, 0.15)'"
            @click="ChagePublic"
          >
            Public
          </b-button>

          <b-button
            variant="primary"
            :to="{ name: 'enquiry-client', params: { clientId: clientId } }"
          >
            <feather-icon icon="PlusIcon" class="mr-50" size="14" />
            Add Enquiry
          </b-button>

          <b-form-checkbox-group
            v-model="filterLook"
            :options="optionsLooking"
            name="filter-looking"
          />
        </b-col>

        <b-col cols="12" md="3" class="d-flex align-items-center justify-content-end">
          <b-form-input
            v-model="searchQuery"
            placeholder="Enquiry / ID"
          />
        </b-col>
      </b-row>
    </div>

    <!-- ── Table ── -->
    <b-table
      ref="refListTable"
      :items="fetchData"
      responsive
      striped
      :fields="tableColumns"
      primary-key="id"
      :sort-by.sync="sortBy"
      show-empty
      empty-text="No matching records found"
      :sort-desc.sync="isSortDirDesc"
      class="position-relative"
    >
      <!-- Column filter row -->
      <template #top-row="{ fields }">
        <td v-for="field in fields" :key="field.key">
          <b-form-input v-if="field.key === 'id'"        v-model="filterId"    :placeholder="field.label" style="width:50px" />
          <b-form-input v-if="field.key === 'code'"      v-model="filterCode"  :placeholder="field.label" />
          <b-form-input v-if="field.key === 'place_name'" v-model="filterplace" placeholder="Project name" />
          <b-form-input v-if="field.key === 'zone_name'" v-model="filterzone"  :placeholder="field.label" />
          <b-form-input v-if="field.key === 'bedroom'"   v-model="filterbr"    :placeholder="field.label" style="width:50px" />
          <b-form-input v-if="field.key === 'owner'"     v-model="filterOwner" :placeholder="field.label" />
          <b-form-input v-if="field.key === 'min'"       v-model="filterMin"   :placeholder="field.label" style="width:80px" />
          <b-form-input v-if="field.key === 'max'"       v-model="filterMax"   :placeholder="field.label" style="width:80px" />
        </td>
      </template>

      <!-- Copy -->
      <template #cell(copy)="data">
        <b-link :to="{ name: 'enquiry-client', params: { id: data.item.id } }">
          <feather-icon icon="CopyIcon" class="text-primary" />
        </b-link>
      </template>

      <!-- Enquiry Code -->
      <template #cell(code)="data">
        <div class="d-flex align-items-center flex-wrap mb-25" style="gap:0.25rem">
          <feather-icon
            icon="StarIcon"
            class="cursor-pointer"
            :stroke="data.item.is_fav ? '#ff9f43' : 'currentColor'"
            :fill="data.item.is_fav ? '#ff9f43' : 'none'"
            @click="change_fav(data.item.id, !data.item.is_fav)"
          />
          <b-link :to="{ name: 'enquiry-edit', params: { id: data.item.id } }">
            {{ data.item.code }}
          </b-link>
          <b-badge v-if="data.item.looking" :variant="lookingVariant(data.item.looking)">
            {{ data.item.looking }}
          </b-badge>
        </div>
        <div v-if="data.item.title" class="small font-weight-bold text-muted">{{ data.item.title }}</div>
      </template>

      <!-- Client / Owner -->
      <template #cell(owner)="data">
        <b-link :to="{ name: 'clients-edit', params: { id: data.item.user_id } }" class="font-weight-bold d-block">
          {{ data.item.firstname }} {{ data.item.lastname }}
        </b-link>
        <a v-if="data.item.email" :href="'mailto:'+data.item.email" class="small d-block text-muted">
          {{ data.item.email }}
        </a>
        <a v-if="data.item.phone" :href="'tel:'+data.item.phone" class="small d-block text-muted">
          {{ data.item.phone }}
        </a>
      </template>

      <!-- Min budget -->
      <template #cell(min)="data">
        <span v-if="data.item.rent_min > 0">{{ data.item.rent_min | getcomma }}</span>
        <span v-else-if="data.item.buy_min > 0">{{ data.item.buy_min | getcomma }}</span>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- Max budget -->
      <template #cell(max)="data">
        <span v-if="data.item.rent_max > 0">{{ data.item.rent_max | getcomma }}</span>
        <span v-else-if="data.item.buy_max > 0">{{ data.item.buy_max | getcomma }}</span>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- Public toggle -->
      <template #cell(is_public)="data">
        <b-form-checkbox
          :checked="data.item.is_public == 1"
          class="custom-control-primary"
          switch
          @change="change_public(data.item.id, $event)"
        />
      </template>

      <!-- Actions -->
      <template #cell(actions)="data">
        <b-dropdown variant="link" toggle-class="text-decoration-none" no-caret>
          <template #button-content>
            <feather-icon icon="MoreVerticalIcon" size="16" class="text-body" />
          </template>
          <b-dropdown-item
            v-if="data.item.created_by == userData.id || allow_delete.includes(userData.type_id)"
            :to="{ name: 'enquiry-edit', params: { id: data.item.id } }"
          >
            <feather-icon icon="Edit2Icon" class="mr-50" />
            Edit
          </b-dropdown-item>
          <b-dropdown-item
            v-if="allow_delete.includes(userData.type_id)"
            @click="deleteconfirm(data.item.id)"
          >
            <feather-icon icon="TrashIcon" class="mr-50" />
            Delete
          </b-dropdown-item>
        </b-dropdown>
      </template>

    </b-table>

    <!-- ── Pagination ── -->
    <div class="mx-2 mb-2">
      <b-row>
        <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
          <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span>
        </b-col>
        <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
          <b-pagination
            v-model="currentPage"
            :total-rows="totals"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            prev-class="prev-item"
            next-class="next-item"
          >
            <template #prev-text><feather-icon icon="ChevronLeftIcon" size="18" /></template>
            <template #next-text><feather-icon icon="ChevronRightIcon" size="18" /></template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>

    <!-- ── Message Sidebar ── -->
    <b-sidebar
      id="sidebar-enquiry-messages"
      bg-variant="white"
      v-model="isSidebarcommentOpen"
      no-header
      right
      backdrop
      shadow
      class="side-bar-responsive"
    >
      <template>
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h5 class="mb-0">Messages</h5>
          <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="formcomment_close" />
        </div>
        <messages :id="eq_id" v-if="isSidebarcommentOpen" v-on:childToClose="onSubmitClose" />
      </template>
    </b-sidebar>

  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BFormCheckbox, BFormCheckboxGroup,
  BButton, BTable, BLink, BBadge, BDropdown, BDropdownItem,
  BPagination, BSidebar,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store'
import { onUnmounted } from '@vue/composition-api'
import useEnquiryListClient from './useEnquiryClient'
import enquiryStoreModule from '../enquiryStoreModule'
import Ripple from 'vue-ripple-directive'
import Messages from '../../components/comments/Messages.vue'
import router from '@/router'

const LOOKING_VARIANTS = { Rent: 'light-info', Sale: 'light-success' }

export default {
  components: {
    BCard, BRow, BCol, BFormInput, BFormCheckbox, BFormCheckboxGroup,
    BButton, BTable, BLink, BBadge, BDropdown, BDropdownItem,
    BPagination, BSidebar, vSelect, Messages,
  },

  directives: { Ripple },

  props: {
    presetId: {
      type: [String, Number],
      default: null,
    },
  },

  filters: {
    getcomma(value) {
      if (!value) return ''
      return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
  },

  data() {
    return {
      isSidebarcommentOpen: false,
      userData: JSON.parse(localStorage.getItem('userData')),
      allow_delete: [1, 2],
      clientId: this.presetId || router.currentRoute.params.id,
      eq_id: '',
    }
  },

  setup(props) {
    const ENQUIRY_APP_STORE_MODULE_NAME = 'app-enquiry'
    if (!store.hasModule(ENQUIRY_APP_STORE_MODULE_NAME)) store.registerModule(ENQUIRY_APP_STORE_MODULE_NAME, enquiryStoreModule)
    onUnmounted(() => {
      if (store.hasModule(ENQUIRY_APP_STORE_MODULE_NAME)) store.unregisterModule(ENQUIRY_APP_STORE_MODULE_NAME)
    })

    const {
      fetchData,
      tableColumns,
      perPage,
      currentPage,
      totals,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      sortFav,
      sortPublic,
      ChageFav,
      ChagePublic,
      optionsLooking,
      isSortDirDesc,
      refListTable,
      filterMin,
      filterMax,
      filterCode,
      filterplace,
      filterLook,
      filterzone,
      filterbr,
      filterOwner,
      filterId,
      refetchData,
      UpdateFav,
      UpdatePublic,
      deleteData,
    } = useEnquiryListClient(props.presetId)

    return {
      fetchData,
      tableColumns,
      perPage,
      currentPage,
      totals,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      filterCode,
      filterplace,
      filterzone,
      filterbr,
      filterLook,
      filterOwner,
      filterId,
      filterMin,
      filterMax,
      sortFav,
      sortPublic,
      ChageFav,
      ChagePublic,
      optionsLooking,
      refetchData,
      UpdateFav,
      UpdatePublic,
      deleteData,
    }
  },

  methods: {
    lookingVariant(looking) {
      return LOOKING_VARIANTS[looking] || 'light-secondary'
    },

    change_fav(id, is_fav) {
      this.UpdateFav(id, is_fav ? 1 : 0)
      this.refetchData()
    },

    change_public(id, is_public) {
      this.UpdatePublic(id, is_public ? 1 : 0)
      this.refetchData()
    },

    formcomment_show(id) {
      this.eq_id = id
      this.isSidebarcommentOpen = true
    },
    formcomment_close() {
      this.isSidebarcommentOpen = false
    },
    onSubmitClose() {
      this.isSidebarcommentOpen = false
      this.refetchData()
    },

    deleteconfirm(id) {
      this.$bvModal.msgBoxConfirm('Please confirm that you want to delete this enquiry.', {
        title: 'Confirm Delete',
        size: 'sm',
        okVariant: 'danger',
        okTitle: 'Delete',
        cancelTitle: 'Cancel',
        cancelVariant: 'outline-secondary',
        hideHeaderClose: false,
        centered: true,
      }).then(confirmed => {
        if (confirmed) this.deleteData(id)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.cursor-pointer {
  cursor: pointer;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
